import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import { Hooks, Platform } from "@xt/xtbase";
import { usePWAInstall } from '@/hooks/usePWAInstall';
import { isLandingPathname } from '@/utils/landingRoute';
const { useTranslation } = Hooks;

const PWA_INSTALL_DISMISSED_KEY = 'pwa_install_dismissed';
// 套壳/内嵌场景：URL 带此参数时本 session 内不弹 PWA 安装提示（同源多页面/多项目跳转均生效）
const NO_PWA_PROMPT_PARAM = 'noPwaPrompt';
const NO_PWA_PROMPT_SESSION_KEY = 'no_pwa_prompt';

const PWAInstallPrompt = () => {
  const router = useRouter();
  const { t } = useTranslation();
  const { install, canInstall, isInstalled, isIOS } = usePWAInstall();
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      // 若 URL 带有 noPwaPrompt 参数，写入 sessionStorage，本 session 内页面/项目跳转都不再弹
      const params = new URLSearchParams(window.location.search);
      if (params.has(NO_PWA_PROMPT_PARAM)) {
        sessionStorage.setItem(NO_PWA_PROMPT_SESSION_KEY, '1');
      }
    }
  }, []);

  useEffect(() => {
    // 检查localStorage，每天弹一次
    if (typeof window !== 'undefined') {
      const dismissed = localStorage.getItem(PWA_INSTALL_DISMISSED_KEY);
      if (dismissed) {
        // 兼容旧数据：如果是 'true'，清除它
        if (dismissed === 'true') {
          localStorage.removeItem(PWA_INSTALL_DISMISSED_KEY);
        } else {
          // 检查时间戳，判断是否超过24小时（一天）
          const dismissedTime = parseInt(dismissed, 10);
          const now = Date.now();
          const oneDay = 24 * 60 * 60 * 1000; // 24小时的毫秒数
          if (now - dismissedTime < oneDay) {
            // 24小时内不再显示
            return;
          }
          // 超过24小时，清除标记，允许再次显示
          localStorage.removeItem(PWA_INSTALL_DISMISSED_KEY);
        }
      }
    }

    if (typeof window !== 'undefined') {
      const path = window.location.pathname;
      if (isLandingPathname(path)) {
        setShowInstallPrompt(false);
        return;
      }
    }

    if (canInstall && !isInstalled) {
      setShowInstallPrompt(true);
    }
  }, [canInstall, isInstalled, router.asPath]);

  const handleInstallClick = () => {
    const iosInstructions = isIOS
      ? t('pwa.install.ios.instruction') || 'In Safari, tap the share button at the bottom, then select "Add to Home Screen"'
      : null;
    install(iosInstructions);
    setShowInstallPrompt(false);
    // 记录当前时间戳到localStorage，24小时内不再弹出
    if (typeof window !== 'undefined') {
      localStorage.setItem(PWA_INSTALL_DISMISSED_KEY, Date.now().toString());
    }
  };

  const handleDismiss = () => {
    setShowInstallPrompt(false);
    // 记录当前时间戳到localStorage，24小时内不再弹出
    if (typeof window !== 'undefined') {
      localStorage.setItem(PWA_INSTALL_DISMISSED_KEY, Date.now().toString());
    }
  };

  // 环境判断：PWA 内不弹、App 内 WebView 不弹、URL 带 noPwaPrompt 或 session 已记录（套壳/内嵌）不弹；landing 页不弹
  if (typeof window !== 'undefined') {
    if (Platform.isInApp()) return null;
    if (sessionStorage.getItem(NO_PWA_PROMPT_SESSION_KEY)) return null;
    if (new URLSearchParams(window.location.search).has(NO_PWA_PROMPT_PARAM)) return null;
    if (isLandingPathname(window.location.pathname)) return null;
  }
  if (isInstalled || !showInstallPrompt) {
    return null;
  }

  return (
    <>
      <style>{`
        @keyframes slideUp {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .pwa-install-prompt {
          position: fixed;
          bottom: 0;
          left: 0;
          right: 0;
          z-index: 10000;
          padding: 12px 16px;
          background: #fff;
          box-shadow: 0 -2px 12px rgba(0, 0, 0, 0.1);
          border-top: 1px solid rgba(0, 0, 0, 0.06);
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          animation: slideUp 0.3s ease-out;
          max-width: 100%;
          box-sizing: border-box;
        }
        [data-theme='dark'] .pwa-install-prompt {
          background: #1B1B1E;
          border-top-color: rgba(255, 255, 255, 0.1);
        }
        [data-theme='dark'] .pwa-install-prompt .pwa-prompt-title {
          color: #fff;
        }
        [data-theme='dark'] .pwa-install-prompt .pwa-prompt-desc {
          color: rgba(255, 255, 255, 0.6);
        }
        /* H5移动端适配 */
        @media (max-width: 767px) {
          .pwa-install-prompt {
            padding: 12px;
            flex-direction: column;
            align-items: stretch;
            gap: 12px;
          }
          .pwa-prompt-content {
            flex-direction: row !important;
            align-items: center !important;
            gap: 12px !important;
            width: 100%;
          }
          .pwa-prompt-icon {
            width: 36px !important;
            height: 36px !important;
            font-size: 18px !important;
            flex-shrink: 0;
          }
          .pwa-prompt-text {
            flex: 1;
            min-width: 0;
            overflow: hidden;
          }
          .pwa-prompt-title {
            font-size: 14px !important;
            margin-bottom: 2px !important;
            line-height: 20px !important;
          }
          .pwa-prompt-desc {
            font-size: 12px !important;
            line-height: 16px !important;
            white-space: normal !important;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
            text-overflow: ellipsis;
          }
          .pwa-prompt-actions {
            width: 100% !important;
            display: flex !important;
            align-items: center !important;
            justify-content: space-between !important;
            gap: 8px !important;
            flex-shrink: 0;
          }
          .pwa-prompt-actions button {
            flex: 1;
            min-width: 0;
            padding: 10px 16px !important;
            font-size: 14px !important;
            white-space: nowrap;
          }
          .pwa-prompt-close {
            flex: 0 0 32px !important;
            width: 32px !important;
            height: 32px !important;
            padding: 0 !important;
          }
          .pwa-prompt-cancel {
            flex: 0 0 auto !important;
            padding: 10px 12px !important;
            min-width: 60px;
          }
        }
        /* 超小屏幕适配 */
        @media (max-width: 375px) {
          .pwa-install-prompt {
            padding: 10px;
          }
          .pwa-prompt-title {
            font-size: 13px !important;
          }
          .pwa-prompt-desc {
            font-size: 11px !important;
          }
          .pwa-prompt-actions button {
            padding: 8px 12px !important;
            font-size: 13px !important;
          }
        }
      `}</style>
      <div className="pwa-install-prompt">
        <div className="pwa-prompt-content"
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            minWidth: 0,
          }}
        >
          <div className="pwa-prompt-icon"
            style={{
              flexShrink: 0,
              width: '40px',
              height: '40px',
              borderRadius: '8px',
              background: 'linear-gradient(135deg, #B9F78A 0%, #3aa865 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '20px',
            }}
          >
            📱
          </div>
          <div className="pwa-prompt-text" style={{ flex: 1, minWidth: 0 }}>
            <div className="pwa-prompt-title"
              style={{
                fontSize: '14px',
                fontWeight: 600,
                color: '#333',
                marginBottom: '4px',
                lineHeight: '20px',
              }}
            >
              {t('pwa.install.title') || 'Install App'}
            </div>
            <div className="pwa-prompt-desc"
              style={{
                fontSize: '12px',
                color: '#666',
                lineHeight: '16px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {t('pwa.install.description') || 'Add the app to your home screen for a better experience!'}
            </div>
          </div>
        </div>
        <div className="pwa-prompt-actions"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            flexShrink: 0,
          }}
        >
          <button
            className="pwa-prompt-cancel"
            onClick={handleDismiss}
            style={{
              padding: '6px 12px',
              border: 'none',
              background: 'transparent',
              color: '#999',
              fontSize: '14px',
              cursor: 'pointer',
              borderRadius: '4px',
              transition: 'all 0.2s',
              WebkitTapHighlightColor: 'transparent',
            }}
            onMouseEnter={(e) => {
              e.target.style.background = 'rgba(0, 0, 0, 0.05)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'transparent';
            }}
            onTouchStart={(e) => {
              e.target.style.background = 'rgba(0, 0, 0, 0.05)';
            }}
            onTouchEnd={(e) => {
              e.target.style.background = 'transparent';
            }}
          >
            {t('pwa.install.cancel') || 'Later'}
          </button>
          <button
            onClick={handleInstallClick}
            style={{
              padding: '8px 20px',
              border: 'none',
              borderRadius: '6px',
              background: '#B9F78A',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.2s',
              whiteSpace: 'nowrap',
              WebkitTapHighlightColor: 'transparent',
              touchAction: 'manipulation',
            }}
            onMouseEnter={(e) => {
              e.target.style.background = '#3aa865';
              e.target.style.transform = 'scale(1.02)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = '#B9F78A';
              e.target.style.transform = 'scale(1)';
            }}
            onTouchStart={(e) => {
              e.target.style.background = '#3aa865';
              e.target.style.transform = 'scale(0.98)';
            }}
            onTouchEnd={(e) => {
              e.target.style.background = '#B9F78A';
              e.target.style.transform = 'scale(1)';
            }}
          >
            {t('pwa.install.button') || 'Install'}
          </button>
          <button
            className="pwa-prompt-close"
            onClick={handleDismiss}
            style={{
              padding: '4px',
              border: 'none',
              background: 'transparent',
              color: '#999',
              fontSize: '18px',
              cursor: 'pointer',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '24px',
              height: '24px',
              transition: 'all 0.2s',
              WebkitTapHighlightColor: 'transparent',
            }}
            onMouseEnter={(e) => {
              e.target.style.background = 'rgba(0, 0, 0, 0.05)';
              e.target.style.color = '#333';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'transparent';
              e.target.style.color = '#999';
            }}
            onTouchStart={(e) => {
              e.target.style.background = 'rgba(0, 0, 0, 0.05)';
              e.target.style.color = '#333';
            }}
            onTouchEnd={(e) => {
              e.target.style.background = 'transparent';
              e.target.style.color = '#999';
            }}
          >
            ×
          </button>
        </div>
      </div>
    </>
  );
};

export default PWAInstallPrompt;

